import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const BlogModal = ({
  isOpen,
  onClose,
  title = 'Titre du modal',
  logoSrc,
  description = 'Texte descriptif du contenu du modal.',
  ctaText = 'Visiter le site',
  ctaHref = 'https://www.i-neea.ch/',
}) => {
  const overlayRef = useRef(null);
  const closeBtnRef = useRef(null);
  const lastFocusedRef = useRef(null);

  // Mount portal root if needed
  const modalRoot =
    document.getElementById('modal-root') ||
    (function () {
      const el = document.createElement('div');
      el.id = 'modal-root';
      document.body.appendChild(el);
      return el;
    })();

  useEffect(() => {
    if (!isOpen) return;
    // Save last focused element and lock scroll
    lastFocusedRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // ESC to close
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);

    // Focus the close button when open
    requestAnimationFrame(() => {
      closeBtnRef.current?.focus();
    });

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      // Restore focus
      if (lastFocusedRef.current && lastFocusedRef.current.focus) {
        lastFocusedRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  // Close on outside click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div>
      <style>{`
        /* Palette inspirée par i-NEEA : fond clair, accents verts, contrastes nets */
        :root {
          --ineea-green: #2ea04b;   /* vert “énergie” */
          --ineea-green-dark: #23823c;
          --ineea-ink: #0f1720;     /* texte principal très lisible */
          --ineea-muted: #5b6a75;   /* texte secondaire */
          --ineea-bg: #ffffff;
          --ring: 0 0 0 3px rgba(46,160,75,0.3);
        }

        .ineea-overlay {
          position: fixed;
          inset: 0;
          display: grid;
          place-items: center;
          background: rgba(15, 23, 32, 0.25);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 9999;
          padding: 24px;
        }

        .ineea-modal {
          position: relative;
          width: min(720px, 100%);
          background: var(--ineea-bg);
          color: var(--ineea-ink);
          border-radius: 16px;
          box-shadow:
            0 20px 45px rgba(0,0,0,0.18),
            0 2px 10px rgba(0,0,0,0.06);
          overflow: hidden;
        }

        .ineea-header {
          position: relative;
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          padding: 20px 24px 16px 24px;
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        .ineea-logo {
          height: 28px;
          width: auto;
          justify-self: start;
        }

        .ineea-title {
          margin: 0;
          justify-self: center;
          font-size: 20px;
          line-height: 1.2;
          font-weight: 700;
          letter-spacing: 0.2px;
        }

        .ineea-close {
          justify-self: end;
          border: none;
          background: transparent;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: inline-grid;
          place-items: center;
          cursor: pointer;
        }
        .ineea-close:hover {
          background: rgba(0,0,0,0.05);
        }
        .ineea-close:focus-visible {
          outline: var(--ring);
        }

        .ineea-body {
          padding: 24px;
          font-size: 16px;
          line-height: 1.6;
          color: var(--ineea-ink);
        }

        .ineea-desc {
          margin: 0;
          color: var(--ineea-muted);
          font-size: 16px;
        }

        .ineea-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px 24px 24px;
        }

        .ineea-cta {
          appearance: none;
          -webkit-appearance: none;
          border: none;
          border-radius: 12px;
          padding: 12px 16px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          background: var(--ineea-green);
          color: white;
          transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .ineea-cta:hover {
          background: var(--ineea-green-dark);
          transform: translateY(-1px);
        }
        .ineea-cta:focus-visible {
          outline: var(--ring);
        }

        /* Icône close (SVG) réutilisable */
        .ineea-x {
          width: 18px; height: 18px; display:block;
          stroke: currentColor; stroke-width: 2; fill: none;
        }

        @media (max-width: 480px) {
          .ineea-title { font-size: 18px; }
          .ineea-body  { font-size: 15px; }
        }
      `}</style>

      <div
        className="ineea-overlay"
        ref={overlayRef}
        onMouseDown={handleOverlayClick}
        role="presentation"
      >
        <div
          className="ineea-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ineea-modal-title"
        >
          <header className="ineea-header">
            <div style={{ minWidth: 0 }}>
              {logoSrc ? (
                <img className="ineea-logo" src={logoSrc} alt="Logo" />
              ) : (
                <span aria-hidden="true" />
              )}
            </div>

            <h2 id="ineea-modal-title" className="ineea-title">
              {title}
            </h2>

            <button
              className="ineea-close"
              onClick={onClose}
              aria-label="Fermer"
              ref={closeBtnRef}
              type="button"
            >
              <svg className="ineea-x" viewBox="0 0 24 24">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="ineea-body">
            <p className="ineea-desc">{description}</p>
          </div>

          <div className="ineea-footer">
            <a
              className="ineea-cta"
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ctaText}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M7 17L17 7M17 7H9M17 7v8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, modalRoot);
};

BlogModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  logoSrc: PropTypes.string,
  description: PropTypes.string,
  ctaText: PropTypes.string,
  ctaHref: PropTypes.string,
};

export default BlogModal;
